import { useCallback, useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { SideRail } from './design/SideRail';
import { NewPcoModal } from './NewPcoModal';
import { FeedbackBubble } from './FeedbackBubble';
import { useSettingsPcos } from '../lib/useSettings';
import { useInbox } from '../lib/useInbox';
import { getRailPinned, setRailPinned as persistRailPinned } from '../lib/railPref';

/**
 * Authenticated shell — side rail nav + main content.
 *
 * The shell owns the side rail's PCO list + the reconciliation open-count
 * hook. Hosting these here once avoids re-firing on every page navigation;
 * pages that need the same data still call their own hooks (small acceptable
 * duplication; no shared fetch cache in v1 per CLAUDE.md's "no state library"
 * rule).
 *
 * The rail's `New PCO` button dispatches into the NewPcoModal that also lives
 * here, so any authenticated page can pop it without extra wiring. It opens
 * the same three-option chooser the PCO Log's "Add PCO" does — the rail keeps
 * the shorter label because it sits in a narrow column, not because the two
 * offer different things.
 */
export function AppShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const pcos = useSettingsPcos();
  const inbox = useInbox();
  const [createOpen, setCreateOpen] = useState(false);

  // The rail's inbox count and Recent-PCOs ranking both read the data hosted
  // here. Refresh it on every navigation so saving blocks → going to review
  // re-floats the destination PCO in the rail's ranking, and the count stays
  // current — no shared cache in v1. `refetch` from useApiQuery is stable.
  const refetchNav = useCallback(() => {
    void pcos.refetch();
    void inbox.refetch();
    // pcos.refetch / inbox.refetch are stable (useApiQuery pins the fetcher to a
    // ref; `run` is useCallback([token])). Depending on the `pcos`/`inbox` objects
    // themselves — what exhaustive-deps suggests — would rebuild refetchNav every
    // render and fire the nav effect on every render, the opposite of intended.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pcos.refetch, inbox.refetch]);

  useEffect(() => {
    refetchNav();
  }, [location.pathname, refetchNav]);

  // Rail width — two independent numbers, one class each, because they are only
  // equal at rest:
  //   .rail-collapsed  the CONTENT's gutter is 64px. Follows the pin, which is
  //                    a device preference remembered across sessions.
  //   .rail-narrow     the RAIL is 64px. Follows hover/focus, reported up by
  //                    the rail itself.
  // A hover overlay is precisely the state where they disagree — the rail is
  // wide, the gutter is not — so it needs no third flag and nothing reflows.
  const [railPinned, setRailPinned] = useState(getRailPinned);
  const [railNarrow, setRailNarrow] = useState(() => !getRailPinned());
  const togglePin = useCallback(() => setRailPinned((v) => !v), []);
  useEffect(() => {
    persistRailPinned(railPinned);
  }, [railPinned]);

  const shellClass = [
    'app-shell',
    railPinned ? '' : 'rail-collapsed',
    railNarrow ? 'rail-narrow' : '',
  ]
    .filter(Boolean)
    .join(' ');

  // PDFs with REVIEW WORK LEFT — not every row in the list. Since 2026-08-03
  // the list also carries fully-processed dailies whose independent re-read is
  // still flagged, so the flag stays reachable after the work is done. Those
  // need a look, not a review; counting them here would make one red badge mean
  // two different things.
  const inboxCount = inbox.data?.pdfs.filter((p) => p.blocks_pending > 0).length;

  return (
    <div className={shellClass}>
      <SideRail
        pcos={pcos.data ?? []}
        inboxCount={inboxCount}
        onCreatePco={() => setCreateOpen(true)}
        pinned={railPinned}
        onTogglePin={togglePin}
        onNarrowChange={setRailNarrow}
      />
      <main>
        <Outlet context={{ refetchNav }} />
      </main>

      <NewPcoModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={async (newPco) => {
          setCreateOpen(false);
          await pcos.refetch();
          navigate(`/pcos/${newPco.id}`);
        }}
      />

      <FeedbackBubble pcos={pcos.data ?? []} />
    </div>
  );
}
